import React, { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Table,
  Switch,
  Typography,
  Popconfirm,
  Alert,
  message,
  Spin,
  Modal,
  Badge,
  Tooltip,
  Empty,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CopyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ListPageHeader from '@shared/ListPageHeader';
import { integrationService } from '@services/admin/integrationService';
import { webhookService } from '@services/admin/webhookService';

const { Text } = Typography;

const statusColors = {
  success: 'success',
  failed: 'error',
  pending: 'processing',
  retrying: 'warning',
};

export default function AdminIntegrationShow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    fetchIntegration();
  }, [id]);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const data = await integrationService.get(id);
      if (data.success && data.data) {
        setIntegration(data.data);
      }
    } catch (error) {
      message.error(t('shared.integrations.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const data = await integrationService.toggleActive(id);
      if (data.success) {
        message.success(t('shared.integrations.messages.toggle_success'));
        fetchIntegration();
      }
    } catch (error) {
      message.error(t('shared.integrations.error.update_failed'));
    }
  };

  const handleDelete = async () => {
    try {
      const data = await integrationService.delete(id);
      if (data.success) {
        message.success(t('shared.integrations.messages.delete_success'));
        navigate('/integrations');
      }
    } catch (error) {
      message.error(t('shared.integrations.error.delete_failed'));
    }
  };

  const handleRegenerateToken = async () => {
    try {
      const data = await integrationService.regenerateToken(id);
      if (data.success) {
        message.success(t('shared.integrations.messages.regenerate_success'));
        fetchIntegration();
      }
    } catch (error) {
      message.error(t('shared.integrations.error.update_failed'));
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    message.success(t('shared.integrations.messages.copy_success'));
  };

  // ==================== Webhook actions ====================
  const webhook = integration?.webhook;

  const handleWebhookToggleActive = async () => {
    if (!webhook) return;
    try {
      const data = await webhookService.toggleActive(webhook.id);
      if (data.success) {
        message.success(t('shared.webhooks.messages.toggle_success'));
        fetchIntegration();
      }
    } catch (error) {
      message.error(t('shared.webhooks.error.update_failed'));
    }
  };

  const handleWebhookTest = async () => {
    if (!webhook) return;
    try {
      setTestLoading(true);
      const data = await webhookService.test(webhook.id);
      if (data.success) {
        const result = data.data;
        Modal.info({
          title: t('shared.webhooks.test_result.title'),
          content: (
            <Descriptions column={1} size="small" style={{ marginTop: 16 }}>
              <Descriptions.Item label={t('shared.webhooks.test_result.status')}>
                <Badge
                  status={result.status === 'success' ? 'success' : 'error'}
                  text={result.status}
                />
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.webhooks.test_result.response_code')}>
                {result.response_status || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.webhooks.test_result.duration')}>
                {result.duration_ms ? `${result.duration_ms}ms` : 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          ),
        });
        fetchIntegration();
      }
    } catch (error) {
      message.error(t('shared.webhooks.messages.test_failed'));
    } finally {
      setTestLoading(false);
    }
  };

  const handleWebhookRegenerateSecret = async () => {
    if (!webhook) return;
    try {
      const data = await webhookService.regenerateSecret(webhook.id);
      if (data.success) {
        message.success(t('shared.webhooks.messages.regenerate_success'));
        fetchIntegration();
      }
    } catch (error) {
      message.error(t('shared.webhooks.error.update_failed'));
    }
  };

  const deliveryColumns = [
    {
      title: t('shared.webhooks.fields.events'),
      dataIndex: 'event_type',
      key: 'event_type',
      render: (text) => <Tag>{t(`shared.webhooks.events.${text}`, text)}</Tag>,
    },
    {
      title: t('shared.webhooks.test_result.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge status={statusColors[status] || 'default'} text={status} />
      ),
    },
    {
      title: t('shared.webhooks.test_result.response_code'),
      dataIndex: 'response_status',
      key: 'response_status',
      render: (code) => (code ? <Tag color={code >= 200 && code < 300 ? 'green' : 'red'}>{code}</Tag> : '-'),
    },
    {
      title: t('shared.webhooks.test_result.duration'),
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      render: (ms) => (ms ? `${ms}ms` : '-'),
    },
    {
      title: t('shared.webhooks.fields.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!integration) {
    return null;
  }

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={['developer_tools', 'developer_tools.integrations', { label: integration.name }]}
        actions={
          <Space>
            <Switch
              checked={integration.active}
              onChange={handleToggleActive}
              checkedChildren={t('shared.integrations.status.active')}
              unCheckedChildren={t('shared.integrations.status.inactive')}
            />
            <Button icon={<EditOutlined />} onClick={() => navigate(`/integrations/${id}/edit`)}>
              {t('common.button.edit')}
            </Button>
            <Popconfirm
              title={t('shared.integrations.confirm.delete')}
              onConfirm={handleDelete}
              okText={t('common.button.confirm')}
              cancelText={t('common.button.cancel')}
            >
              <Button danger icon={<DeleteOutlined />}>
                {t('common.button.delete')}
              </Button>
            </Popconfirm>
          </Space>
        }
      />

      {/* Basic Info */}
      <Card title={t('shared.integrations.detail.basic_info')} style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={t('shared.integrations.fields.name')}>
            {integration.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.integrations.fields.active')}>
            <Badge
              status={integration.active ? 'success' : 'default'}
              text={integration.active ? t('shared.integrations.status.active') : t('shared.integrations.status.inactive')}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.integrations.fields.description')} span={2}>
            {integration.description || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.integrations.fields.created_at')}>
            {new Date(integration.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.integrations.fields.last_used_at')}>
            {integration.last_used_at ? new Date(integration.last_used_at).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* API Credentials */}
      <Card title={t('shared.integrations.detail.credentials')} style={{ marginBottom: 16 }}>
        <Alert
          message={t('shared.integrations.detail.credentials_description')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Alert
          message={t('shared.integrations.detail.token_security_notice')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label={t('shared.integrations.fields.client_id')}>
            <Space>
              <Text code>{integration.client_id}</Text>
              <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(integration.client_id)}>
                {t('shared.integrations.actions.copy')}
              </Button>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.integrations.fields.access_token')}>
            <Space>
              <Text code>
                {showToken && integration.access_token ? integration.access_token : '••••••••••••••••••••••••••••••••'}
              </Text>
              <Button
                size="small"
                icon={showToken ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? t('shared.integrations.actions.hide_token') : t('shared.integrations.actions.show_token')}
              </Button>
              {integration.access_token && (
                <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(integration.access_token)}>
                  {t('shared.integrations.actions.copy')}
                </Button>
              )}
              <Popconfirm
                title={t('shared.integrations.confirm.regenerate_token')}
                onConfirm={handleRegenerateToken}
                okText={t('common.button.confirm')}
                cancelText={t('common.button.cancel')}
              >
                <Button size="small" icon={<ReloadOutlined />}>
                  {t('shared.integrations.actions.regenerate_token')}
                </Button>
              </Popconfirm>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.integrations.fields.ip_whitelist')}>
            {(integration.ip_whitelist || []).length > 0 ? (
              <Space wrap size={[8, 8]}>
                {integration.ip_whitelist.map((ip) => (
                  <Tag key={ip}>{ip}</Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">{t('shared.integrations.detail.ip_whitelist_unrestricted')}</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Scopes */}
      <Card title={t('shared.integrations.detail.scopes')} style={{ marginBottom: 16 }}>
        <Space wrap size={[8, 8]}>
          {(integration.scopes || []).map((scope) => (
            <Tag key={scope} color="blue">
              {t(`shared.integrations.scopes.${scope.replace(':', '_')}`, scope)}
            </Tag>
          ))}
        </Space>
      </Card>

      {/* Webhook Endpoint (1:1) */}
      <Card
        title={t('shared.integrations.detail.webhook')}
        style={{ marginBottom: 16 }}
        extra={
          webhook && (
            <Space>
              <Switch
                checked={webhook.active}
                onChange={handleWebhookToggleActive}
                checkedChildren={<CheckCircleOutlined />}
                unCheckedChildren={<CloseCircleOutlined />}
                size="small"
              />
              <Button
                size="small"
                icon={<SendOutlined />}
                loading={testLoading}
                onClick={handleWebhookTest}
              >
                {t('shared.webhooks.actions.test')}
              </Button>
            </Space>
          )
        }
      >
        {webhook ? (
          <>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label={t('shared.webhooks.fields.url')} span={2}>
                <Text code copyable>{webhook.url}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.webhooks.fields.events')} span={2}>
                <Space wrap size={[4, 4]}>
                  {(webhook.events || []).map((event) => (
                    <Tag key={event} color="blue">
                      {t(`shared.webhooks.events.${event}`, event)}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.webhooks.detail.signature_info')}>
                <Space>
                  <Text code>{webhook.secret_key || '********'}</Text>
                  <Popconfirm
                    title={t('shared.webhooks.confirm.regenerate_secret')}
                    onConfirm={handleWebhookRegenerateSecret}
                    okText={t('common.button.confirm')}
                    cancelText={t('common.button.cancel')}
                  >
                    <Button size="small" icon={<ReloadOutlined />}>
                      {t('shared.webhooks.actions.regenerate_secret')}
                    </Button>
                  </Popconfirm>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.webhooks.fields.deliveries_count')}>
                {webhook.deliveries_count || 0}
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : (
          <Empty
            description={t('shared.integrations.detail.no_webhook')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button onClick={() => navigate(`/integrations/${id}/edit`)}>
              {t('shared.integrations.actions.configure_webhook')}
            </Button>
          </Empty>
        )}
      </Card>
    </div>
  );
}
